import { priceGroupOf } from '@restaurant/types';
import type { Product } from '@restaurant/api-contracts';

export interface DisplayPrice {
  /** The amount to render. */
  amount: number;
  /**
   * True when `amount` is a floor (final price depends on option choices) -
   * the UI should render it with a "from" prefix.
   */
  isFrom: boolean;
}

/**
 * Resolve what to show as a product's starter price on a list / card.
 *
 * Rules, applied in order:
 *
 * 1. If the product has a price group (base price 0, priced through its first
 *    required single-select option - see `priceGroupOf`), show the lowest
 *    choice price in that group. That choice is the real minimum the guest
 *    will pay, but other choices may cost more, so `isFrom: true`. With no
 *    price group, the base price (0) is used and `isFrom` only flips true
 *    when some other option has a priced choice.
 * 2. Otherwise show the base price. `isFrom` is true when at least one option
 *    choice anywhere adds a non-zero extra - the displayed price is then a
 *    floor, not fixed.
 */
export function getDisplayPrice(product: Pick<Product, 'price' | 'options'>): DisplayPrice {
  const { price, options } = product;
  const hasPricedChoice = options.some((o) => o.choices.some((c) => c.price > 0));

  const priceGroupId = priceGroupOf({ price, options });
  const priceGroup = priceGroupId ? options.find((o) => o.id === priceGroupId) : undefined;
  if (priceGroup && priceGroup.choices.length > 0) {
    const min = Math.min(...priceGroup.choices.map((c) => c.price));
    return { amount: min, isFrom: true };
  }

  return { amount: price, isFrom: hasPricedChoice };
}
